import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { axiosClient } from '../../../library/Service/axiosClient';

const OutreachUploadLayout = (props: any) => {
    const [file, setFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [responseMessage, setResponseMessage] = useState<{ processed: number; invalid: number } | null>(null);
    interface InvalidRecord {
        record: any;
        errors: string[];
    }
    const [invalidData, setInvalidData] = useState<InvalidRecord[]>([]);
    const navigate = useNavigate();
    const location = useLocation();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!file) {
            console.error('No file selected');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        const url = `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/upload-outreach/upload-spread-sheet`;

        try {
            setIsLoading(true);
            setUploadProgress(0);

            let uploadResponse = await axiosClient().post(url, formData, {
                onUploadProgress: (progressEvent) => {
                    if (progressEvent && progressEvent.loaded !== undefined && progressEvent.total !== undefined) {
                        const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                        setUploadProgress(progress);
                    } else {
                        console.warn('Progress information is missing or incomplete.');
                        setUploadProgress(0);
                    }
                },
            });

            const { message, invalidRecords, data, invalidData } = uploadResponse.data;
            setInvalidData(invalidData || []);
            setResponseMessage({
                processed: data.length,
                invalid: invalidRecords,
            });

            setIsFormSubmitted(true);
            console.log('Outreach data uploaded successfully');
        } catch (error) {
            console.error('Error uploading file:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container max-1140 px-lg-0 overflow-hidden">
            <div className="row">
                <div className="col-md-12 mb-4">
                    <div className="container mt-5">
                        <h2 className="mb-4">Bulk Upload Outreach Data</h2>

                        <div className="alert alert-info">
                            <h5>Please Note:</h5>
                            <ul>
                                <li>The file must be in Excel format (.xlsx).</li>
                                <li>
                                    Required columns: <strong>institute_id</strong>, <strong>student_uuid</strong>, <strong>date</strong>, <strong>team</strong>, <strong>team_member_email</strong>, <strong>purpose</strong>, <strong>sub_purpose</strong>, <strong>disposition</strong>, <strong>talked_to</strong>, <strong>mode</strong>.
                                </li>
                                <li>
                                    Optional columns: <strong>comment</strong>, <strong>duration</strong>.
                                </li>
                                <li>
                                    <strong>student_uuid</strong> must match a registered user in the system.
                                </li>
                                <li>
                                    <strong>team_member_email</strong> must match a registered user in the system.
                                </li>
                            </ul>
                            <a href="https://company-logo-list.s3.ap-south-1.amazonaws.com/Bulk-Outreach-Sample-File.xlsx" className="btn btn-outline-primary" download>
                                Download Sample Outreach Excel File
                            </a>
                        </div>

                        <form onSubmit={handleSubmit} encType="multipart/form-data">
                            <div className="mb-3">
                                <label htmlFor="fileInput" className="form-label">
                                    Choose file (Only Excel)
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="fileInput"
                                    accept=".xlsx"
                                    onChange={handleFileChange}
                                    required
                                />
                            </div>
                            {!isFormSubmitted && (
                                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                    {isLoading ? 'Uploading...' : 'Upload'}
                                </button>
                            )}
                        </form>
                        {isLoading && (
                            <div className="progress mt-3">
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{ width: `${uploadProgress}%` }}
                                    aria-valuenow={uploadProgress}
                                    aria-valuemin={0}
                                    aria-valuemax={100}>
                                    {uploadProgress}%
                                </div>
                            </div>
                        )}
                        {responseMessage && (
                            <div className="mt-4">
                                <h5>Upload Summary</h5>
                                <p>
                                    <strong>Valid Records inserted in DB:</strong> {responseMessage.processed}
                                </p>
                                <p>
                                    <strong>Invalid Records:</strong> {responseMessage.invalid}
                                </p>
                            </div>
                        )}
                        {invalidData.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                                <h3>Invalid Data</h3>
                                <table className="table" style={{ minWidth: '800px' }}>
                                    <thead>
                                        <tr>
                                            <th scope="col">Record</th>
                                            <th scope="col">Errors</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invalidData.map((item, index) => (
                                            <tr key={index}>
                                                <td>{JSON.stringify(item.record)}</td>
                                                <td>{item.errors.join(', ')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OutreachUploadLayout;
